import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { SEOHead } from '@/components/seo';
import { Header, Footer, FeesSection, FilingsSection } from '@/components/public';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_NAME = 'StartEase Agent';
const BASE_URL = 'https://starteaseai.com';
const APP_URL = '/login';

// Palette tokens for /usa/ — match homepage + /ai-document-services
const BG = 'bg-[#0b1310]';
const BG_PANEL = 'bg-[#111827]';
const BG_BAND = 'bg-[#070d0b]';
const BORDER = 'border-white/10';

const ALL_STATES = [
  { slug: 'wyoming', name: 'Wyoming', code: 'WY', fee: 100, highlight: 'No Income Tax', popular: true },
  { slug: 'delaware', name: 'Delaware', code: 'DE', fee: 90, highlight: 'VC Preferred', popular: true },
  { slug: 'texas', name: 'Texas', code: 'TX', fee: 300, highlight: 'No Income Tax', popular: true },
  { slug: 'florida', name: 'Florida', code: 'FL', fee: 125, highlight: 'Gateway to LATAM', popular: true },
  { slug: 'nevada', name: 'Nevada', code: 'NV', fee: 425, highlight: 'Strong Privacy', popular: false },
  { slug: 'california', name: 'California', code: 'CA', fee: 70, highlight: 'Tech Hub', popular: false },
  { slug: 'new-york', name: 'New York', code: 'NY', fee: 200, highlight: 'Financial Capital', popular: false },
  { slug: 'colorado', name: 'Colorado', code: 'CO', fee: 50, highlight: 'Lowest Fees', popular: false },
  { slug: 'washington', name: 'Washington', code: 'WA', fee: 200, highlight: 'No Income Tax', popular: false },
  { slug: 'georgia', name: 'Georgia', code: 'GA', fee: 100, highlight: 'Southeast Hub', popular: false },
];

const HOW_IT_WORKS = [
  { step: 1, title: 'Select Jurisdiction',  description: 'Choose the state that best aligns with your business objectives.' },
  { step: 2, title: 'Submit Information',   description: 'Complete our streamlined application. Takes approximately 10 minutes.' },
  { step: 3, title: 'Document Preparation', description: 'Our team prepares and files all required formation documents.' },
  { step: 4, title: 'Receive Documentation', description: 'Receive your approved documents, EIN, and compliance calendar.' },
];

const MonoLabel = ({ children }) => (
  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#059669]">
    {children}
  </span>
);

const USAHubPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes] = await Promise.all([
          axios.get(`${API}/public/services`),
        ]);
        setServices(servicesRes.data.services || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const jsonLdSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': BASE_URL },
      { '@type': 'ListItem', 'position': 2, 'name': 'US Company Formation', 'item': `${BASE_URL}/usa/` },
    ],
  };

  return (
    <div className={`min-h-screen ${BG} text-white`} data-testid="usa-hub-page">
      {/* SEO byte-identical per spec */}
      <SEOHead
        title="US Company Formation Services | LLC & C-Corporation"
        description="Professional US business formation services. LLC, C-Corp, EIN, Registered Agent. All 50 states."
        siteName={SITE_NAME}
        canonical={`${BASE_URL}/usa/`}
        keywords="US company formation, LLC formation, C-Corp formation, EIN application"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLdSchema)}</script>
      </Helmet>

      <Header />

      {/* ─── HERO ────────────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden pt-16 md:pt-20 pb-16 md:pb-20"
        data-testid="usa-hero-section"
      >
        {/* Grid background like homepage hero */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            WebkitMaskImage: 'radial-gradient(ellipse 75% 65% at 15% 30%, #000 30%, transparent 78%)',
            maskImage: 'radial-gradient(ellipse 75% 65% at 15% 30%, #000 30%, transparent 78%)',
          }}
          aria-hidden="true"
        />
        <div className="container-custom max-w-7xl relative">
          <nav className="mb-10" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-slate-500">
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li className="text-white font-medium">US Company Formation</li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <MonoLabel>Formation Services</MonoLabel>
            <h1 className="font-serif text-5xl md:text-6xl font-semibold text-white tracking-tight leading-[1.07] mt-4 mb-6">
              US Business Formation Services
            </h1>
            <p className="text-lg md:text-xl text-slate-400 leading-relaxed mb-8 max-w-2xl">
              Establish your US LLC or C-Corporation in any jurisdiction. We handle the documentation,
              compliance, and ongoing maintenance requirements.
            </p>
            <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#059669]" />
                <span>All 50 States</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#059669]" />
                <span>7-10 Day Timeline</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#059669]" />
                <span>Expert Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SERVICES ──────────────────────────────────────────────────── */}
      <section
        className={`${BG_BAND} border-y ${BORDER} py-20 md:py-24`}
        data-testid="usa-services-section"
      >
        <div className="container-custom max-w-7xl">
          <div className="max-w-3xl mb-12 md:mb-14">
            <MonoLabel>Our Services</MonoLabel>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight leading-[1.12] mt-4 mb-4">
              Formation &amp; Compliance Services
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed">
              From initial formation to ongoing compliance, we provide comprehensive entity management services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              [...Array(6)].map((_, i) => (
                <div key={i} className={`${BG_PANEL} border ${BORDER} rounded-xl p-6 animate-pulse`}>
                  <div className="h-6 bg-white/10 rounded w-3/4 mb-4" />
                  <div className="h-4 bg-white/5 rounded w-full mb-2" />
                </div>
              ))
            ) : (
              services.map((service) => (
                <Link
                  key={service.slug}
                  to={`/usa/${service.slug}/`}
                  className={`${BG_PANEL} border ${BORDER} rounded-xl p-6 hover:border-[#059669]/50 transition-colors group block`}
                  data-testid={`usa-service-card-${service.slug}`}
                >
                  <h3 className="text-[15.5px] font-semibold text-white mb-2 group-hover:text-[#059669] transition-colors">
                    {service.name}
                  </h3>
                  <p className="text-slate-400 text-sm mb-5 leading-relaxed line-clamp-2">
                    {service.short_description || service.description}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-white/5">
                    <span className="font-serif text-[22px] text-[#059669] leading-none">
                      From ${service.base_price}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-[#059669] transition-colors" />
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>

      {/* ─── JURISDICTIONS ──────────────────────────────────────────────── */}
      <section
        className={`${BG} border-b ${BORDER} py-20 md:py-24`}
        data-testid="usa-states-section"
      >
        <div className="container-custom max-w-7xl">
          <div className="max-w-3xl mb-12 md:mb-14">
            <MonoLabel>Jurisdictions</MonoLabel>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight leading-[1.12] mt-4 mb-4">
              Formation Jurisdictions
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed">
              Each jurisdiction offers distinct advantages. We provide guidance on selecting the optimal state for your business requirements.
            </p>
          </div>

          {/* Recommended */}
          <div className="mb-12">
            <h3 className="font-mono text-[10.5px] uppercase tracking-[0.2em] text-slate-500 mb-5">Recommended Jurisdictions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {ALL_STATES.filter((s) => s.popular).map((state) => (
                <Link
                  key={state.slug}
                  to={`/usa/states/${state.slug}/`}
                  className={`${BG_PANEL} border ${BORDER} rounded-xl p-6 hover:border-[#059669]/50 transition-colors group block`}
                  data-testid={`usa-state-card-${state.slug}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-serif text-2xl font-semibold text-[#059669]">{state.code}</span>
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-1">{state.name}</h4>
                  <p className="text-slate-500 text-sm mb-3">{state.highlight}</p>
                  <p className="text-slate-300 font-medium text-sm">LLC from ${state.fee}</p>
                </Link>
              ))}
            </div>
          </div>

          {/* All */}
          <div>
            <h3 className="font-mono text-[10.5px] uppercase tracking-[0.2em] text-slate-500 mb-5">All Jurisdictions</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {ALL_STATES.filter((s) => !s.popular).map((state) => (
                <Link
                  key={state.slug}
                  to={`/usa/states/${state.slug}/`}
                  className={`${BG_PANEL} border ${BORDER} rounded-lg p-4 text-center hover:border-[#059669]/50 transition-colors group`}
                  data-testid={`usa-state-card-${state.slug}`}
                >
                  <span className="text-lg font-semibold text-slate-300 group-hover:text-[#059669] transition-colors">
                    {state.code}
                  </span>
                  <p className="text-xs text-slate-500 mt-0.5">{state.name}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROCESS ────────────────────────────────────────────────────── */}
      <section
        className={`${BG_BAND} border-b ${BORDER} py-20 md:py-24`}
        data-testid="usa-how-it-works-section"
      >
        <div className="container-custom max-w-7xl">
          <div className="max-w-3xl mb-12 md:mb-14">
            <MonoLabel>Process</MonoLabel>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight leading-[1.12] mt-4 mb-4">
              Formation Process
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed">
              A streamlined four-step process designed for efficiency and clarity.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {HOW_IT_WORKS.map((item) => (
              <div
                key={item.step}
                className={`${BG_PANEL} border ${BORDER} rounded-xl p-6`}
                data-testid={`usa-step-${item.step}`}
              >
                <p className="font-serif text-[30px] text-[#059669]/85 leading-none">
                  {String(item.step).padStart(2, '0')}
                </p>
                <h3 className="text-[15px] font-semibold text-white mt-3.5 mb-2">{item.title}</h3>
                <p className="text-[13.5px] text-slate-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <a
              href={APP_URL}
              className="inline-flex items-center gap-2 bg-[#059669] hover:bg-[#047857] text-white font-semibold text-sm md:text-[15px] px-6 py-3.5 rounded-lg transition-colors"
              data-testid="usa-how-it-works-cta"
            >
              Begin Formation <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ─── PRICING BUNDLE + FILINGS ($448 + amendments) ────────────────── */}
      {/* NOTE: FeesSection and FilingsSection are shared components used on
          multiple pages. Wrapping them in dark section wrappers so the surrounding
          band matches; internal light-theme surfaces are still theirs. Marked as
          a next-round reskin candidate — see PRD. */}
      <div className={BG} data-testid="usa-fees-wrap">
        <FeesSection entityType="llc" />
      </div>
      <div className={BG_BAND} data-testid="usa-filings-wrap">
        <FilingsSection entityType="general" />
      </div>

      {/* ─── FINAL CTA ──────────────────────────────────────────────────── */}
      <section
        className={`${BG_BAND} border-t ${BORDER} py-20 md:py-24`}
        data-testid="usa-cta-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight leading-[1.12] mb-5">
            Ready to Establish Your US Entity?
          </h2>
          <p className="text-lg text-slate-400 mb-9 max-w-2xl mx-auto leading-relaxed">
            Join thousands of global entrepreneurs who trust {SITE_NAME} for professional business formation services.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3.5 justify-center">
            <a
              href={APP_URL}
              className="inline-flex items-center gap-2 bg-[#059669] hover:bg-[#047857] text-white font-semibold text-sm md:text-[15px] px-6 py-3.5 rounded-lg transition-colors"
              data-testid="usa-cta-primary"
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="https://calendly.com/santoshdealdocs/us-busines-setup"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/10 hover:border-[#059669]/60 text-white font-semibold text-sm md:text-[15px] px-6 py-3.5 rounded-lg transition-colors"
              data-testid="usa-cta-secondary"
            >
              Schedule Consultation
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default USAHubPage;
